import * as yup from 'yup';
import type { CreativeEntry } from 'types/shared';
import type { PinterestCampaignWithAdGroups, PinterestPinText } from '../api/types';

export const PINTEREST_STEP = {
  CREATIVES: 0,
  ACCOUNT: 1,
  CAMPAIGNS: 2,
  AD_GROUPS: 3,
  AD: 4,
} as const;

export const PINTEREST_VALIDATION_MESSAGE = {
  creatives: 'At least one creative has to be selected',
  account: "Can't be empty",
  campaigns: 'At least one campaign has to be selected',
  adGroups: 'At least one ad group has to be selected',
  adGroupsPerCampaign:
    'At least one ad group must be selected for each selected campaign that has ad groups with a flow',
  pinTexts: 'Default pin text is missing for some of the selected flows',
} as const;

/** Distinct flows of the selected ad groups, from the server snapshot. */
export const getSelectedPinterestFlows = (
  adGroupsData: readonly PinterestCampaignWithAdGroups[] | undefined,
  selectedAdGroups: readonly string[],
): string[] => {
  const selected = new Set(selectedAdGroups);
  return [
    ...new Set(
      (adGroupsData ?? []).flatMap((campaign) =>
        campaign.ad_groups
          .filter((adGroup) => selected.has(adGroup.ad_group_id) && !!adGroup.flow)
          .map((adGroup) => adGroup.flow!),
      ),
    ),
  ].sort();
};

export const pinterestSchema = yup
  .object()
  .shape({
    currentStep: yup.number().default(0).optional(),
    app: yup.string().required("Can't be empty"),
    accountId: yup.string().default(''),
    selectedCreatives: yup.array().of(yup.string().required()).default([]),
    selectedCreativesOnly: yup.bool().default(false),
    filterCreativeByName: yup.array().of(yup.string().required()).default([]),
    selectedCampaigns: yup.array().of(yup.string().required()).default([]),
    selectedCampaignsOnly: yup.bool().default(false),
    filterCampaignsByName: yup.array().of(yup.string().required()).default([]),
    selectedAdGroups: yup.array().of(yup.string().required()).default([]),
    filterAdGroupsByName: yup.array().of(yup.string().required()).default([]),
    $creativesRows: yup.mixed<CreativeEntry[]>().optional(),
    $adGroupsData: yup.mixed<PinterestCampaignWithAdGroups[]>().optional(),
    $pinTextsData: yup.mixed<Record<string, PinterestPinText | undefined>>().default({}),
  })
  .test('step-validation', 'Step validation failed', function (value) {
    const currentStep = value.currentStep ?? 0;

    if (currentStep === PINTEREST_STEP.CREATIVES && value.selectedCreatives.length === 0) {
      return this.createError({ path: 'selectedCreatives', message: PINTEREST_VALIDATION_MESSAGE.creatives });
    }

    if (currentStep === PINTEREST_STEP.ACCOUNT && !value.accountId) {
      return this.createError({ path: 'accountId', message: PINTEREST_VALIDATION_MESSAGE.account });
    }

    if (currentStep === PINTEREST_STEP.CAMPAIGNS && value.selectedCampaigns.length === 0) {
      return this.createError({ path: 'selectedCampaigns', message: PINTEREST_VALIDATION_MESSAGE.campaigns });
    }

    if (currentStep === PINTEREST_STEP.AD_GROUPS) {
      if (value.selectedAdGroups.length === 0) {
        return this.createError({ path: 'selectedAdGroups', message: PINTEREST_VALIDATION_MESSAGE.adGroups });
      }

      const adGroupsData = value.$adGroupsData;
      if (Array.isArray(adGroupsData)) {
        const selected = new Set(value.selectedAdGroups);
        // A campaign whose ad groups all lack a parsed flow has nothing selectable,
        // so it cannot be required to have a selection.
        const everyCampaignCovered = value.selectedCampaigns.every((campaignId) => {
          const campaign = adGroupsData.find((entry) => entry.campaign_id === campaignId);
          if (!campaign) return true;

          const selectable = campaign.ad_groups.filter((adGroup) => !!adGroup.flow);
          if (!selectable.length) return true;

          return selectable.some((adGroup) => selected.has(adGroup.ad_group_id));
        });

        if (!everyCampaignCovered) {
          return this.createError({
            path: 'selectedAdGroups',
            message: PINTEREST_VALIDATION_MESSAGE.adGroupsPerCampaign,
          });
        }
      }
    }

    if (currentStep === PINTEREST_STEP.AD) {
      const flows = getSelectedPinterestFlows(value.$adGroupsData, value.selectedAdGroups);
      const pinTexts = value.$pinTextsData ?? {};
      if (flows.some((flow) => !pinTexts[flow])) {
        return this.createError({ path: '$pinTextsData', message: PINTEREST_VALIDATION_MESSAGE.pinTexts });
      }
    }

    return true;
  });

export type PinterestFormValues = yup.InferType<typeof pinterestSchema>;
